import React from 'react';
import { motion } from 'framer-motion';
import { FaShieldAlt } from 'react-icons/fa';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-nature-leaf text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-block mb-4"
          >
            <FaShieldAlt className="text-6xl" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-4xl md:text-5xl font-bold mb-4"
          >
            Privacy Policy
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-lg opacity-90"
          >
            Last Updated: January 2026
          </motion.p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="prose prose-lg max-w-none"
        >
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <p className="text-gray-700 leading-relaxed mb-6">
              At Navyaa The Home Stay, we respect your privacy and are committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, store, and safeguard your information when you visit our website or interact with us for bookings and enquiries.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may collect the following information when you use our website or services:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Name</li>
              <li>Phone number</li>
              <li>Email address</li>
              <li>Booking details such as stay dates and preferences</li>
              <li>Any information shared through enquiry forms, WhatsApp, or calls</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              We do not collect sensitive personal data unless required for booking or legal compliance.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The information collected is used to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Process booking enquiries and reservations</li>
              <li>Communicate booking confirmations and stay related information</li>
              <li>Respond to your queries or requests</li>
              <li>Improve our services and guest experience</li>
              <li>Comply with legal or regulatory requirements</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              We do not sell, rent, or trade your personal information to third parties.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Protection and Security</h2>
            <p className="text-gray-700 leading-relaxed">
              We take reasonable steps to protect your personal information from unauthorized access, misuse, or disclosure. Your data is stored securely and accessed only by authorized personnel for legitimate business purposes.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookies and Website Tracking</h2>
            <p className="text-gray-700 leading-relaxed">
              Our website may use cookies to enhance user experience and analyze website traffic. Cookies do not collect personal information and can be disabled through your browser settings if you prefer.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Third Party Services</h2>
            <p className="text-gray-700 leading-relaxed">
              We may use third party tools or platforms such as booking engines, payment gateways, or analytics services. These third parties have their own privacy policies, and we recommend reviewing them separately.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Sharing of Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Your personal information may be shared only when:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Required to complete a booking or service</li>
              <li>Mandated by law or government authorities</li>
              <li>Necessary to protect the rights and safety of the homestay or guests</li>
            </ul>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Consent</h2>
            <p className="text-gray-700 leading-relaxed">
              By using our website or submitting your details, you consent to the collection and use of your information as outlined in this Privacy Policy.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Updates to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time. Any changes will be posted on this page and will be effective immediately upon posting.
            </p>
          </div>

          <div className="bg-gradient-to-br from-primary-500 to-nature-leaf text-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-4">Contact Information</h2>
            <p className="leading-relaxed mb-4">
              If you have any questions or concerns regarding this Privacy Policy or how your data is handled, please contact us at:
            </p>
            <p className="font-semibold text-lg">Navyaa The Home Stay</p>
            <p>Email: mohiteji@gmail.com</p>
            <p>Phone: +91 94200 84600</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
